/**
 * Abstract base class for all devtools UI field components.
 *
 * Each field type creates its own DOM, handles user interaction,
 * and emits value changes through a callback. The field is fully
 * self-contained — no external framework required.
 */
export abstract class StringDevUIField<T = any> {
  protected readonly key: string;
  protected readonly label: string;
  protected value: T;
  protected onChange: (key: string, value: T) => void;
  protected root: HTMLElement | null = null;

  constructor(
    key: string,
    label: string,
    defaultValue: T,
    onChange: (key: string, value: T) => void,
  ) {
    this.key = key;
    this.label = label;
    this.value = defaultValue;
    this.onChange = onChange;
  }

  /** Builds and returns the DOM element for this field */
  abstract build(): HTMLElement;

  /** Updates the field's visual state from an external value */
  abstract setValue(value: T): void;

  /** Updates the field disabled state from external conditions */
  setDisabled(disabled: boolean): void {
    if (!this.root) return;
    this.root.setAttribute("data-disabled", disabled ? "true" : "false");
  }

  /** Returns the current value */
  getValue(): T {
    return this.value;
  }

  /** Removes DOM and cleans up event listeners */
  destroy(): void {
    this.root?.remove();
    this.root = null;
  }

  /** Emits the current value through the callback */
  protected emit(value: T): void {
    this.value = value;
    this.onChange(this.key, value);
  }

  /** Creates a label element with consistent styling */
  protected createLabel(): HTMLLabelElement {
    const label = document.createElement("label");
    label.textContent = this.label;
    label.setAttribute("data-stdg-label", "");
    return label;
  }

  /** Creates a row wrapper for label + input */
  protected createRow(): HTMLDivElement {
    const row = document.createElement("div");
    row.setAttribute("data-stdg-panel-field", "");
    return row;
  }
}
